import { useState } from "react";
import { getPropertyGroups, type ElementRef } from "../model/inp/properties.ts";
import type { SwmmProject } from "../model/inp/types.ts";
import { fieldLabelKey, sectionLabelKey } from "../i18n/fieldLabels.ts";
import { useT } from "../i18n/hooks.ts";
import { Dialog } from "./Dialog.tsx";
import { DialogActions } from "./DialogActions.tsx";

interface PropertyOption {
  section: string;
  columnIndex: number;
  label: string;
}

// INFILTRATION is excluded: its column meaning depends on the project's
// [OPTIONS] INFILTRATION method (sectionSchema.ts), so a single dropdown
// entry can't safely stand in for it.
export function GroupEditDialog({
  project,
  selection,
  onApply,
  onClose,
}: {
  project: SwmmProject;
  selection: ElementRef[];
  onApply: (section: string, columnIndex: number, value: string) => void;
  onClose: () => void;
}) {
  const t = useT();
  const groups = getPropertyGroups(project, selection[0]).filter(
    (g) => g.section !== "INFILTRATION"
  );
  const options: PropertyOption[] = groups.flatMap((g) => {
    const sectionKey = sectionLabelKey(g.section);
    const sectionLabel = sectionKey ? t(sectionKey) : g.label;
    return g.properties.map((p) => {
      const fieldKey = fieldLabelKey(g.section, p.label);
      const fieldLabel = fieldKey ? t(fieldKey) : p.label;
      return {
        section: g.section,
        columnIndex: p.columnIndex,
        label: `${sectionLabel} — ${fieldLabel}`,
      };
    });
  });

  const [choice, setChoice] = useState<string>(
    options[0] ? `${options[0].section}:${options[0].columnIndex}` : ""
  );
  const [value, setValue] = useState("");

  function apply() {
    const [section, columnIndexStr] = choice.split(":");
    if (!section) return;
    onApply(section, Number(columnIndexStr), value);
  }

  return (
    <Dialog title={t("groupEdit.title", { count: selection.length })} onClose={onClose}>
      {options.length === 0 ? (
        <p className="property-panel-hint">{t("groupEdit.noProperties")}</p>
      ) : (
        <>
          <label className="options-field">
            {t("groupEdit.property")}
            <select value={choice} onChange={(e) => setChoice(e.target.value)}>
              {options.map((o) => (
                <option
                  key={`${o.section}:${o.columnIndex}`}
                  value={`${o.section}:${o.columnIndex}`}
                >
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="options-field">
            {t("groupEdit.newValue")}
            <input type="text" value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
          </label>
        </>
      )}
      <DialogActions
        primary={{ label: t("groupEdit.apply"), onClick: apply, disabled: options.length === 0 }}
        onClose={onClose}
      />
    </Dialog>
  );
}
